import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RefreshCw, FileText, Trash2, AlertCircle } from 'lucide-react';
import { ordersService, OrderWithLinesDto, UpdateOrderLineRequest } from '../../../../services/ordersService';
import { BackButton } from '../../../common/BackButton';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';

import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, TableDataRow, RowAction, SummaryCalculation } from '../../../shared/table';
import { useInitialLoading } from '../../../../hooks/useInitialLoading';

// Editable field names for order lines (matches keys in UpdateOrderLineRequest)
type EditableLineTextField =
  | 'orderLineFreeField1' | 'orderLineFreeField2' | 'orderLineFreeField3'
  | 'orderLineFreeField4' | 'orderLineFreeField5' | 'orderLineFreeField6'
  | 'orderLineFreeField7' | 'orderLineFreeField8' | 'orderLineFreeField9'
  | 'orderLineFreeField10';
type EditableLineNumberField = 'weight' | 'height' | 'length' | 'width' | 'volum';
type EditableLineField = EditableLineTextField | EditableLineNumberField;

// Extended type for table - flattened order line with order header info
interface OrderLineTableRow extends TableDataRow {
  id: string; // Composite key: orderNo-lineNo
  lineDbId: number; // Database ID needed for delete API
  pListID: number | null; // PList binding - null/0 = unbound

  // Order header fields
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  orderStatusERP: string | null;
  orderType: string | null;
  pickListNoERP: string | null;
  orderDate: string | null;
  customerNo: string;
  customerName: string | null;
  customerType: string | null;
  customerGrp1: string | null;
  customerGrp2: string | null;
  customerGrp3: string | null;
  delAddressNo: number | null;
  delCustomerName: string | null;
  delAddress1: string | null;
  delAddress2: string | null;
  delAddress3: string | null;
  delZipCode: string | null;
  delCity: string | null;
  delCountryCode: string | null;
  delCountryName: string | null;
  deliveryGLN: string | null;
  customerOrderNo: string | null;
  yourRef: string | null;
  ourRef: string | null;
  projectNo: string | null;
  depNo: string | null;
  deliveryMethod: string | null;
  temsOfDelivery: string | null;
  shipmentType: string | null;
  carrierName: string | null;
  termsOfPayment: string | null;
  employee: string | null;
  orderPriority: number | null;
  customerPriority: number | null;
  district: string | null;
  contactName: string | null;
  orderTotal: number | null;
  emailAddress: string | null;
  orderDeliveryYear: number | null;
  orderDeliveryMonth: number | null;
  orderDeliveryDay: number | null;
  customerDeliveryDay: string | null;
  orderIsRemainderYesNo: boolean | null;
  orderCreateRemainderYesNo: boolean | null;
  orderFreeField1: string | null;
  orderFreeField2: string | null;
  orderFreeField3: string | null;
  orderFreeField4: string | null;
  orderFreeField5: string | null;
  orderFreeField6: string | null;

  // Order line fields
  lineNo: number;
  lineNoERP: number | null;
  subLineNoERP: number | null;
  lineStatus: number;
  itemNo: string;
  itemBarCode: string | null;
  description1: string | null;
  itemGroup1: string | null;
  itemGroup2: string | null;
  itemGroup3: string | null;
  quantity: number | null;
  quantityDelivered: number | null;
  quantityReserved: number | null;
  quantityPicked: number | null;
  itemUnitCode: string | null;
  itemUnitTxt: string | null;
  price: number | null;
  discount: number | null;
  lineDeliveryDate: string | null;
  serialNoItemYesNo: boolean | null;
  stdLocationName: string | null;
  pickZone: string | null;
  warehouse: string | null;
  batchID: string | null;
  expirationDate: string | null;
  weight: number | null;
  height: number | null;
  length: number | null;
  width: number | null;
  volum: number | null;
  itemPictureName: string | null;
  pListBatchYesNo: boolean | null;
  orderLineFreeField1: string | null;
  orderLineFreeField2: string | null;
  orderLineFreeField3: string | null;
  orderLineFreeField4: string | null;
  orderLineFreeField5: string | null;
  orderLineFreeField6: string | null;
  orderLineFreeField7: string | null;
  orderLineFreeField8: string | null;
  orderLineFreeField9: string | null;
  orderLineFreeField10: string | null;
  lineCreateRemainderYesNo: boolean | null;
  lineIsRemainderYesNo: boolean | null;
  isDisplayPallet: boolean | null;
  qtyOnPallet: number | null;
  printSTAND005: boolean | null;
  itemTemperatureInfo: string | null;
  stackingWeight: number | null;
  unCode: string | null;
}

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<OrderLineTableRow>();

interface OrderLinesProps {
  onBack?: () => void;
}

const OrderLines: React.FC<OrderLinesProps> = ({ onBack }) => {
  const { currentWarehouse } = useWarehouse();
  const { t } = useTranslation();
  const [orders, setOrders] = useState<OrderWithLinesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Spinner bare ved første lasting — ved «Oppdater» blir tabellen og filtrene stående (TW 36443150)
  const initialLoading = useInitialLoading(loading);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Kode-baserte, språk-bevisste filteropsjoner for Linjestatus-kolonnen (StatusType 1).
  const lineStatusOptions = useStatusOptions(1);

  // Inline editing state (keyed by composite "orderNo-lineNo")
  const [editState, setEditState] = useState<Record<string, Partial<Record<EditableLineField, string | number>>>>({});
  const [savingLines, setSavingLines] = useState<Set<string>>(new Set());
  const [savedLines, setSavedLines] = useState<Set<string>>(new Set());
  const editStateRef = useRef(editState);
  const ordersRef = useRef(orders);

  // Keep refs in sync
  useEffect(() => { editStateRef.current = editState; }, [editState]);
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  // Save a single field inline edit via API
  const saveLineInlineEdit = useCallback(async (
    orderNo: number,
    lineNo: number,
    field: EditableLineField,
    newFieldValue?: string | number
  ) => {
    const rowKey = `${orderNo}-${lineNo}`;
    const orderWithLines = ordersRef.current.find(o => o.header.orderNo === orderNo);
    if (!orderWithLines) return;
    const line = orderWithLines.lines.find(l => l.lineNo === lineNo);
    if (!line) return;

    const editData = editStateRef.current[rowKey];
    let newValue: string | number | undefined;

    if (newFieldValue !== undefined) {
      newValue = newFieldValue;
    } else if (editData) {
      newValue = editData[field];
    } else {
      return;
    }

    // Check if value actually changed
    const oldValue = (line as any)[field] ?? '';
    if (String(newValue) === String(oldValue)) return;

    try {
      setSavingLines(prev => new Set(prev).add(rowKey));

      // Build partial update request with only the changed field
      const request: UpdateOrderLineRequest = {};
      (request as any)[field] = newValue === '' ? null : newValue;

      await ordersService.updateOrderLine(orderNo, lineNo, request);

      // Update local state
      setOrders(prev => prev.map(o => {
        if (o.header.orderNo !== orderNo) return o;
        return {
          ...o,
          lines: o.lines.map(l => {
            if (l.lineNo !== lineNo) return l;
            return { ...l, [field]: newValue === '' ? null : newValue };
          })
        };
      }));

      // Show saved indicator
      setSavedLines(prev => new Set(prev).add(rowKey));
      setTimeout(() => {
        setSavedLines(prev => {
          const newSet = new Set(prev);
          newSet.delete(rowKey);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Feil ved lagring:', err);
      alert(`${t('manager.modules.orderLines.messages.saveError', {}, 'Feil ved lagring')}: ${err instanceof Error ? err.message : t('manager.modules.orderLines.messages.unknownError', {}, 'Ukjent feil')}`);
    } finally {
      setSavingLines(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowKey);
        return newSet;
      });
    }
  }, [t]);

  // Generic renderer for editable text cells (order line)
  const renderEditableLineText = useCallback((field: EditableLineTextField) => {
    return (value: string | null, row: OrderLineTableRow) => {
      const rowKey = row.id;
      const orderNo = row.orderNo;
      const lineNo = row.lineNo;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingLines.has(rowKey);
      const isSaved = savedLines.has(rowKey);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${rowKey}-${value}`}
            type="text"
            defaultValue={value || ''}
            onChange={(e) => {
              e.stopPropagation();
              setEditState(prev => ({
                ...prev,
                [rowKey]: { ...prev[rowKey], [field]: e.target.value }
              }));
            }}
            onBlur={() => saveLineInlineEdit(orderNo, lineNo, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveLineInlineEdit(orderNo, lineNo, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingLines, savedLines, saveLineInlineEdit]);

  // Generic renderer for editable number cells (order line)
  const renderEditableLineNumber = useCallback((field: EditableLineNumberField) => {
    return (value: number | null, row: OrderLineTableRow) => {
      const rowKey = row.id;
      const orderNo = row.orderNo;
      const lineNo = row.lineNo;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingLines.has(rowKey);
      const isSaved = savedLines.has(rowKey);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${rowKey}-${value}`}
            type="number"
            step="any"
            defaultValue={value ?? ''}
            onChange={(e) => {
              e.stopPropagation();
              const numVal = e.target.value === '' ? '' : parseFloat(e.target.value);
              setEditState(prev => ({
                ...prev,
                [rowKey]: { ...prev[rowKey], [field]: numVal }
              }));
            }}
            onBlur={() => saveLineInlineEdit(orderNo, lineNo, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveLineInlineEdit(orderNo, lineNo, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs text-right border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingLines, savedLines, saveLineInlineEdit]);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersService.getNewOrdersWithLines(currentWarehouse || undefined);
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.orderLines.errors.loadFailed', {}, 'Feil ved lasting av ordrelinjer'));
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete order line handler
  const handleDeleteOrderLine = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await ordersService.deleteOrderLine(deleteConfirm.id);
      setDeleteConfirm(null);
      await loadOrders();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('manager.modules.orderLines.errors.deleteFailed', {}, 'Kunne ikke slette ordrelinje'));
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, t]);

  // Row actions for order line table
  const orderLineRowActions: RowAction<OrderLineTableRow>[] = useMemo(() => [
    {
      id: 'delete-line',
      label: t('manager.modules.orderLines.actions.deleteLine', {}, 'Slett ordrelinje'),
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row: OrderLineTableRow) => !!(row.pListID && row.pListID > 0),
      onClick: (row: OrderLineTableRow) => {
        setDeleteConfirm({
          id: row.lineDbId,
          label: t('manager.modules.orderLines.messages.deleteLineLabel', { '0': row.lineNo, '1': row.itemNo, '2': row.orderNoERP }, `ordrelinje ${row.lineNo} (${row.itemNo}) på ordre ${row.orderNoERP}`)
        });
      }
    }
  ], [t]);

  // Transform orders to flattened order line table format
  const tableData: OrderLineTableRow[] = useMemo(() => {
    const lines: OrderLineTableRow[] = [];

    orders.forEach(orderWithLines => {
      const h = orderWithLines.header;

      orderWithLines.lines.forEach(line => {
        lines.push({
          id: `${h.orderNo}-${line.lineNo}`,
          lineDbId: line.id,
          pListID: line.pListID ?? null,

          // Order header fields
          orderNo: h.orderNo,
          orderNoERP: h.orderNoERP,
          orderStatus: h.orderStatus,
          orderStatusERP: h.orderStatusERP || null,
          orderType: h.orderType || null,
          pickListNoERP: h.pickListNoERP || null,
          orderDate: h.orderDate || null,
          customerNo: h.customerNo,
          customerName: h.customerName || null,
          customerType: h.customerType || null,
          customerGrp1: h.customerGrp1 || null,
          customerGrp2: h.customerGrp2 || null,
          customerGrp3: h.customerGrp3 || null,
          delAddressNo: h.delAddressNo || null,
          delCustomerName: h.delCustomerName || null,
          delAddress1: h.delAddress1 || null,
          delAddress2: h.delAddress2 || null,
          delAddress3: h.delAddress3 || null,
          delZipCode: h.delZipCode || null,
          delCity: h.delCity || null,
          delCountryCode: h.delCountryCode || null,
          delCountryName: h.delCountryName || null,
          deliveryGLN: h.deliveryGLN || null,
          customerOrderNo: h.customerOrderNo || null,
          yourRef: h.yourRef || null,
          ourRef: h.ourRef || null,
          projectNo: h.projectNo || null,
          depNo: h.depNo || null,
          deliveryMethod: h.deliveryMethod || null,
          temsOfDelivery: h.temsOfDelivery || null,
          shipmentType: h.shipmentType || null,
          carrierName: h.carrierName || null,
          termsOfPayment: h.termsOfPayment || null,
          employee: h.employee || null,
          orderPriority: h.orderPriority ?? null,
          customerPriority: h.customerPriority ?? null,
          district: h.district || null,
          contactName: h.contactName || null,
          orderTotal: h.orderTotal ?? null,
          emailAddress: h.emailAddress || null,
          orderDeliveryYear: h.orderDeliveryYear ?? null,
          orderDeliveryMonth: h.orderDeliveryMonth ?? null,
          orderDeliveryDay: h.orderDeliveryDay ?? null,
          customerDeliveryDay: h.customerDeliveryDay || null,
          orderIsRemainderYesNo: h.isRemainderYesNo ?? null,
          orderCreateRemainderYesNo: h.createRemainderYesNo ?? null,
          orderFreeField1: h.orderFreeField1 || null,
          orderFreeField2: h.orderFreeField2 || null,
          orderFreeField3: h.orderFreeField3 || null,
          orderFreeField4: h.orderFreeField4 || null,
          orderFreeField5: h.orderFreeField5 || null,
          orderFreeField6: h.orderFreeField6 || null,

          // Order line fields
          lineNo: line.lineNo,
          lineNoERP: line.lineNoERP ?? null,
          subLineNoERP: line.subLineNoERP ?? null,
          lineStatus: line.lineStatus,
          itemNo: line.itemNo,
          itemBarCode: line.itemBarCode || null,
          description1: line.description1 || null,
          itemGroup1: line.itemGroup1 || null,
          itemGroup2: line.itemGroup2 || null,
          itemGroup3: line.itemGroup3 || null,
          quantity: line.quantity ?? null,
          quantityDelivered: line.quantityDelivered ?? null,
          quantityReserved: line.quantityReserved ?? null,
          quantityPicked: line.quantityPicked ?? null,
          itemUnitCode: line.itemUnitCode || null,
          itemUnitTxt: line.itemUnitTxt || null,
          price: line.price ?? null,
          discount: line.discount ?? null,
          lineDeliveryDate: line.deliveryDate || null,
          serialNoItemYesNo: line.serialNoItemYesNo ?? null,
          stdLocationName: line.stdLocationName || null,
          pickZone: line.pickZone || null,
          warehouse: line.warehouse || null,
          batchID: line.batchID || null,
          expirationDate: line.expirationDate || null,
          weight: line.weight ?? null,
          height: line.height ?? null,
          length: line.length ?? null,
          width: line.width ?? null,
          volum: line.volum ?? null,
          itemPictureName: line.itemPictureName || null,
          pListBatchYesNo: line.pListBatchYesNo ?? null,
          orderLineFreeField1: line.orderLineFreeField1 || null,
          orderLineFreeField2: line.orderLineFreeField2 || null,
          orderLineFreeField3: line.orderLineFreeField3 || null,
          orderLineFreeField4: line.orderLineFreeField4 || null,
          orderLineFreeField5: line.orderLineFreeField5 || null,
          orderLineFreeField6: line.orderLineFreeField6 || null,
          orderLineFreeField7: line.orderLineFreeField7 || null,
          orderLineFreeField8: line.orderLineFreeField8 || null,
          orderLineFreeField9: line.orderLineFreeField9 || null,
          orderLineFreeField10: line.orderLineFreeField10 || null,
          lineCreateRemainderYesNo: null, // Not in DTO - add if needed
          lineIsRemainderYesNo: null, // Not in DTO - add if needed
          isDisplayPallet: line.isDisplayPallet ?? null,
          qtyOnPallet: line.qtyOnPallet ?? null,
          printSTAND005: line.printSTAND005 ?? null,
          itemTemperatureInfo: line.itemTemperatureInfo || null,
          stackingWeight: line.stackingWeight ?? null,
          unCode: line.unCode || null
        });
      });
    });

    return lines;
  }, [orders]);

  // Custom row ID function
  const getRowId = useCallback((row: OrderLineTableRow) => row.id, []);

  // Define columns for order lines table - based on legacy OrderLine.dfm
  const columns: ColumnDefinition<OrderLineTableRow>[] = useMemo(() => [
    // === ORDRELINJE-FELT (hovedkolonner) ===
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.orderLines.columns.orderNoERP', {}, 'Ordrenr.'), {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('lineNo', 'lineNo', t('manager.modules.orderLines.columns.lineNo', {}, 'Linjenr. (int)'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('lineNoERP', 'lineNoERP', t('manager.modules.orderLines.columns.lineNoERP', {}, 'Linjenr. ERP'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('subLineNoERP', 'subLineNoERP', t('manager.modules.orderLines.columns.subLineNoERP', {}, 'Sublinjenr. ERP'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('lineStatus', 'lineStatus', t('manager.modules.orderLines.columns.lineStatus', {}, 'Linjestatus'), {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      // Kode-basert, språk-bevisst filter (verdi=kode, matcher rad-ens lineStatus).
      filterOptions: lineStatusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.lineStatus} statusType={1} />
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', t('manager.modules.orderLines.columns.itemNo', {}, 'Varenr.'), {
      width: 120,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemBarCode', 'itemBarCode', t('manager.modules.orderLines.columns.itemBarCode', {}, 'GTIN'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('description1', 'description1', t('manager.modules.orderLines.columns.description1', {}, 'Varebeskrivelse'), {
      width: 250,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('itemGroup1', 'itemGroup1', t('manager.modules.orderLines.columns.itemGroup1', {}, 'Varegrp. 1'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('itemGroup2', 'itemGroup2', t('manager.modules.orderLines.columns.itemGroup2', {}, 'Varegrp. 2'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('itemGroup3', 'itemGroup3', t('manager.modules.orderLines.columns.itemGroup3', {}, 'Varegrp. 3'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', t('manager.modules.orderLines.columns.quantity', {}, 'Ant. best.'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value)
    }),
    columnFactory.createNumberColumn('quantityDelivered', 'quantityDelivered', t('manager.modules.orderLines.columns.quantityDelivered', {}, 'Ant. tidl. levert'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value),
      visible: false
    }),
    columnFactory.createNumberColumn('quantityReserved', 'quantityReserved', t('manager.modules.orderLines.columns.quantityReserved', {}, 'Ant. res.'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value),
      visible: false
    }),
    columnFactory.createNumberColumn('quantityPicked', 'quantityPicked', t('manager.modules.orderLines.columns.quantityPicked', {}, 'Ant. plukket'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatQuantity(value)
    }),
    columnFactory.createTextColumn('itemUnitCode', 'itemUnitCode', t('manager.modules.orderLines.columns.itemUnitCode', {}, 'Enh. kode'), {
      width: 80,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemUnitTxt', 'itemUnitTxt', t('manager.modules.orderLines.columns.itemUnitTxt', {}, 'Enhet'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createCurrencyColumn('price', 'price', t('manager.modules.orderLines.columns.price', {}, 'Pris'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatCurrency(value)
    }),
    columnFactory.createNumberColumn('discount', 'discount', t('manager.modules.orderLines.columns.discount', {}, 'Rabatt'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('lineDeliveryDate', 'lineDeliveryDate', t('manager.modules.orderLines.columns.lineDeliveryDate', {}, 'Leveringsdato'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('serialNoItemYesNo', 'serialNoItemYesNo', t('manager.modules.orderLines.columns.serialNoItem', {}, 'Serienr. vare'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.orderLines.values.yes', {}, 'Ja') : value === false ? t('manager.modules.orderLines.values.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('stdLocationName', 'stdLocationName', t('warehouse.general.bin', {}, 'Lokasjon'), {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('pickZone', 'pickZone', t('manager.modules.orderLines.columns.pickZone', {}, 'Plukksone'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('warehouse', 'warehouse', t('manager.modules.orderLines.columns.warehouse', {}, 'Lagersted'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('batchID', 'batchID', t('manager.modules.orderLines.columns.batchID', {}, 'Batchnr.'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('expirationDate', 'expirationDate', t('manager.modules.orderLines.columns.expirationDate', {}, 'Batchdato'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('weight', 'weight', t('warehouse.general.weight', {}, 'Vekt'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('weight')
    }),
    columnFactory.createNumberColumn('height', 'height', t('manager.modules.orderLines.columns.height', {}, 'Høyde'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('height')
    }),
    columnFactory.createNumberColumn('length', 'length', t('manager.modules.orderLines.columns.length', {}, 'Lengde'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('length')
    }),
    columnFactory.createNumberColumn('width', 'width', t('manager.modules.orderLines.columns.width', {}, 'Bredde'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('width')
    }),
    columnFactory.createNumberColumn('volum', 'volum', t('manager.modules.orderLines.columns.volum', {}, 'Volum'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineNumber('volum')
    }),
    columnFactory.createTextColumn('itemPictureName', 'itemPictureName', t('manager.modules.orderLines.columns.itemPictureName', {}, 'Bildenavn'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('pListBatchYesNo', 'pListBatchYesNo', t('manager.modules.orderLines.columns.pListBatch', {}, 'Samplukk tillatt'), {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.orderLines.values.yes', {}, 'Ja') : value === false ? t('manager.modules.orderLines.values.no', {}, 'Nei') : '-',
      visible: false
    }),

    // Ordrelinje frifelt
    columnFactory.createTextColumn('orderLineFreeField1', 'orderLineFreeField1', t('manager.modules.orderLines.columns.lineFreeField', { '0': 1 }, 'Linje frifelt 1'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField1')
    }),
    columnFactory.createTextColumn('orderLineFreeField2', 'orderLineFreeField2', t('manager.modules.orderLines.columns.lineFreeField', { '0': 2 }, 'Linje frifelt 2'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField2')
    }),
    columnFactory.createTextColumn('orderLineFreeField3', 'orderLineFreeField3', t('manager.modules.orderLines.columns.lineFreeField', { '0': 3 }, 'Linje frifelt 3'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField3')
    }),
    columnFactory.createTextColumn('orderLineFreeField4', 'orderLineFreeField4', t('manager.modules.orderLines.columns.lineFreeField', { '0': 4 }, 'Linje frifelt 4'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField4')
    }),
    columnFactory.createTextColumn('orderLineFreeField5', 'orderLineFreeField5', t('manager.modules.orderLines.columns.lineFreeField', { '0': 5 }, 'Linje frifelt 5'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField5')
    }),
    columnFactory.createTextColumn('orderLineFreeField6', 'orderLineFreeField6', t('manager.modules.orderLines.columns.lineFreeField', { '0': 6 }, 'Linje frifelt 6'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField6')
    }),
    columnFactory.createTextColumn('orderLineFreeField7', 'orderLineFreeField7', t('manager.modules.orderLines.columns.lineFreeField', { '0': 7 }, 'Linje frifelt 7'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField7')
    }),
    columnFactory.createTextColumn('orderLineFreeField8', 'orderLineFreeField8', t('manager.modules.orderLines.columns.lineFreeField', { '0': 8 }, 'Linje frifelt 8'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField8')
    }),
    columnFactory.createTextColumn('orderLineFreeField9', 'orderLineFreeField9', t('manager.modules.orderLines.columns.lineFreeField', { '0': 9 }, 'Linje frifelt 9'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField9')
    }),
    columnFactory.createTextColumn('orderLineFreeField10', 'orderLineFreeField10', t('manager.modules.orderLines.columns.lineFreeField', { '0': 10 }, 'Linje frifelt 10'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableLineText('orderLineFreeField10')
    }),

    // Displaypall og SSCC
    columnFactory.createTextColumn('isDisplayPallet', 'isDisplayPallet', t('manager.modules.orderLines.columns.isDisplayPallet', {}, 'Displaypall'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.orderLines.values.yes', {}, 'Ja') : value === false ? t('manager.modules.orderLines.values.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('qtyOnPallet', 'qtyOnPallet', t('manager.modules.orderLines.columns.qtyOnPallet', {}, 'Ant. på pall'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('printSTAND005', 'printSTAND005', t('manager.modules.orderLines.columns.printSSCC', {}, 'Generer SSCC'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.orderLines.values.yes', {}, 'Ja') : value === false ? t('manager.modules.orderLines.values.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('itemTemperatureInfo', 'itemTemperatureInfo', t('manager.modules.orderLines.columns.itemTemperatureInfo', {}, 'Temperaturkrav'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('stackingWeight', 'stackingWeight', t('manager.modules.orderLines.columns.stackingWeight', {}, 'Stablevekt'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('unCode', 'unCode', t('manager.modules.orderLines.columns.unCode', {}, 'Varekode'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // === ORDRE-HODE-FELT ===
    columnFactory.createNumberColumn('orderNo', 'orderNo', t('manager.modules.orderLines.columns.orderNo', {}, 'Ordrenr. (int)'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    // orderStatusText-kolonne fjernet 2026-07-02 (WS3): backend sender ikke lenger statustekst.
    // Linjestatus vises via StatusBadge; ordrestatus finnes på ordre-skjermene.
    columnFactory.createTextColumn('pickListNoERP', 'pickListNoERP', t('manager.modules.orderLines.columns.pickListNoERP', {}, 'Plukklistenr.'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderType', 'orderType', t('manager.modules.orderLines.columns.orderType', {}, 'Ordretype'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderStatusERP', 'orderStatusERP', t('manager.modules.orderLines.columns.orderStatusERP', {}, 'Ordrestatus (ERP)'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('orderDate', 'orderDate', t('manager.modules.orderLines.columns.orderDate', {}, 'Ordredato'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerNo', 'customerNo', t('manager.modules.orderLines.columns.customerNo', {}, 'Kundenr.'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerName', 'customerName', t('warehouse.general.customerName', {}, 'Kundenavn'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('delAddressNo', 'delAddressNo', t('manager.modules.orderLines.columns.delAddressNo', {}, 'Lev. adr. nr'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCustomerName', 'delCustomerName', t('manager.modules.orderLines.columns.delCustomerName', {}, 'Lev. kundenavn'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress1', 'delAddress1', t('manager.modules.orderLines.columns.delAddress1', {}, 'Lev. adr. 1'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress2', 'delAddress2', t('manager.modules.orderLines.columns.delAddress2', {}, 'Lev. adr. 2'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress3', 'delAddress3', t('manager.modules.orderLines.columns.delAddress3', {}, 'Lev. adr. 3'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delZipCode', 'delZipCode', t('manager.modules.orderLines.columns.delZipCode', {}, 'Lev. postnr.'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCity', 'delCity', t('manager.modules.orderLines.columns.delCity', {}, 'Lev. poststed'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCountryCode', 'delCountryCode', t('manager.modules.orderLines.columns.delCountryCode', {}, 'Lev. landkode'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCountryName', 'delCountryName', t('manager.modules.orderLines.columns.delCountryName', {}, 'Lev. landnavn'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryGLN', 'deliveryGLN', t('manager.modules.orderLines.columns.deliveryGLN', {}, 'Lev. GLN'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerOrderNo', 'customerOrderNo', t('manager.modules.orderLines.columns.customerOrderNo', {}, 'Oppr. bestnr.'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('yourRef', 'yourRef', t('manager.modules.orderLines.columns.yourRef', {}, 'Deres ref.'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('ourRef', 'ourRef', t('manager.modules.orderLines.columns.ourRef', {}, 'Vår ref.'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('projectNo', 'projectNo', t('manager.modules.orderLines.columns.projectNo', {}, 'Prosjekt'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('depNo', 'depNo', t('manager.modules.orderLines.columns.depNo', {}, 'Avdeling'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryMethod', 'deliveryMethod', t('manager.modules.orderLines.columns.deliveryMethod', {}, 'Leveringsmåte'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('temsOfDelivery', 'temsOfDelivery', t('manager.modules.orderLines.columns.termsOfDelivery', {}, 'Leveringsbetingelser'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('shipmentType', 'shipmentType', t('manager.modules.orderLines.columns.shipmentType', {}, 'Forsendelsesmåte'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', t('warehouse.general.carrier', {}, 'Transportør'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('termsOfPayment', 'termsOfPayment', t('manager.modules.orderLines.columns.termsOfPayment', {}, 'Betalingsbetingelser'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('employee', 'employee', t('manager.modules.orderLines.columns.employee', {}, 'Medarbeider'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderPriority', 'orderPriority', t('manager.modules.orderLines.columns.orderPriority', {}, 'Ordreprioritet'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('customerPriority', 'customerPriority', t('manager.modules.orderLines.columns.customerPriority', {}, 'Kundeprioritet'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('district', 'district', t('manager.modules.orderLines.columns.district', {}, 'Distrikt'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('contactName', 'contactName', t('manager.modules.orderLines.columns.contactName', {}, 'Kontaktperson'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createCurrencyColumn('orderTotal', 'orderTotal', t('manager.modules.orderLines.columns.orderTotal', {}, 'Ordresum'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatCurrency(value),
      visible: false
    }),
    columnFactory.createTextColumn('customerType', 'customerType', t('manager.modules.orderLines.columns.customerType', {}, 'Kundetype'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp1', 'customerGrp1', t('manager.modules.orderLines.columns.customerGrp', { '0': 1 }, 'Kundegrp. 1'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp2', 'customerGrp2', t('manager.modules.orderLines.columns.customerGrp', { '0': 2 }, 'Kundegrp. 2'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp3', 'customerGrp3', t('manager.modules.orderLines.columns.customerGrp', { '0': 3 }, 'Kundegrp. 3'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderIsRemainderYesNo', 'orderIsRemainderYesNo', t('manager.modules.orderLines.columns.isRemainder', {}, 'Er restordre'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.orderLines.values.yes', {}, 'Ja') : value === false ? t('manager.modules.orderLines.values.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('orderCreateRemainderYesNo', 'orderCreateRemainderYesNo', t('manager.modules.orderLines.columns.createRemainder', {}, 'Lag restordre'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.orderLines.values.yes', {}, 'Ja') : value === false ? t('manager.modules.orderLines.values.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('emailAddress', 'emailAddress', t('manager.modules.orderLines.columns.emailAddress', {}, 'E-post'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryYear', 'orderDeliveryYear', t('manager.modules.orderLines.columns.orderDeliveryYear', {}, 'Lev. år'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryMonth', 'orderDeliveryMonth', t('manager.modules.orderLines.columns.orderDeliveryMonth', {}, 'Lev. mnd'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryDay', 'orderDeliveryDay', t('manager.modules.orderLines.columns.orderDeliveryDay', {}, 'Lev. dag'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerDeliveryDay', 'customerDeliveryDay', t('manager.modules.orderLines.columns.customerDeliveryDay', {}, 'Kundeleveringsdag'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Ordre frifelt
    columnFactory.createTextColumn('orderFreeField1', 'orderFreeField1', t('manager.modules.orderLines.columns.orderFreeField', { '0': 1 }, 'Ordre frifelt 1'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField2', 'orderFreeField2', t('manager.modules.orderLines.columns.orderFreeField', { '0': 2 }, 'Ordre frifelt 2'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField3', 'orderFreeField3', t('manager.modules.orderLines.columns.orderFreeField', { '0': 3 }, 'Ordre frifelt 3'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField4', 'orderFreeField4', t('manager.modules.orderLines.columns.orderFreeField', { '0': 4 }, 'Ordre frifelt 4'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField5', 'orderFreeField5', t('manager.modules.orderLines.columns.orderFreeField', { '0': 5 }, 'Ordre frifelt 5'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField6', 'orderFreeField6', t('manager.modules.orderLines.columns.orderFreeField', { '0': 6 }, 'Ordre frifelt 6'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

  ], [renderEditableLineText, renderEditableLineNumber, lineStatusOptions, t]);

  // Summeringsrad i bunnen: antall unike ordre (under Ordrenr.) og sum bestilt antall over
  // synlige (filtrerte) rader. Beregnes av WarehouseTable over processedData.
  const summaryCalculations: SummaryCalculation<OrderLineTableRow>[] = useMemo(() => [
    {
      column: 'orderNoERP' as keyof OrderLineTableRow,
      type: 'custom' as const,
      customCalculation: (rows: OrderLineTableRow[]) => new Set(rows.map(r => r.orderNo)).size
    },
    {
      column: 'quantity' as keyof OrderLineTableRow,
      type: 'sum' as const,
      formatter: (value: number) => ordersService.formatQuantity(value)
    }
  ], []);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadOrders(),
      variant: 'secondary' as const
    }
  ], [t]);

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>{t('manager.modules.orderLines.messages.loading', {}, 'Laster ordrelinjer...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">{t('manager.modules.orderLines.errors.loadFailed', {}, 'Feil ved lasting av ordrelinjer')}</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadOrders}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>{t('manager.modules.orderLines.buttons.retry', {}, 'Prøv igjen')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <FileText size={24} />
            <span>{t('manager.modules.orderLines.title', {}, 'Ordrelinjer')}</span>
          </h1>
          <span className="text-sm text-gray-500">
            {t('manager.modules.orderLines.summaryCount', { '0': tableData.length, '1': orders.length }, `${tableData.length} linjer fra ${orders.length} ordre`)}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showSummary: true,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: false,
            stickyHeader: true,
            stickyFooter: true,
            compactMode: true,
            persistenceKey: 'order-lines-table'
          }}
          summaryCalculations={summaryCalculations}
          getRowId={getRowId}
          tableActions={tableActions}
          rowActions={orderLineRowActions}
          loading={false}
          error={undefined}
          aria-label={t('manager.modules.orderLines.tableAria', {}, 'Ordrelinjer tabell')}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <AlertCircle className="text-red-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">{t('manager.modules.orderLines.deleteModal.title', {}, 'Bekreft sletting')}</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                {t('manager.modules.orderLines.deleteModal.confirm', { '0': deleteConfirm.label }, `Er du sikker på at du vil slette ${deleteConfirm.label}?`)}
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleDeleteOrderLine}
                disabled={deleting}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>{t('manager.modules.orderLines.deleteModal.deleting', {}, 'Sletter...')}</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>{t('common.buttons.delete', {}, 'Slett')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLines;
